import { QuoteIcon } from '@jouzen/ecom-components/icons/QuoteIcon';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { cx } from 'class-variance-authority';
import { getImageProps } from 'next/image';
import { useTranslations } from 'next-intl';

import Image from '@/app/components/Image';
import Motion from '@/app/components/Motion';
import RichText from '@/app/components/RichText';
import useJapaneseRegionalSite from '@/app/experiments/useJapaneseRegionalSite';

import MobileFeature from './MobileFeature';

const WindDown = () => {
  const t = useTranslations();

  const isJapaneseRegionalSite = useJapaneseRegionalSite();

  const mainImage = isJapaneseRegionalSite
    ? 'ja-regional/hp-features-winding-down-desktop'
    : 'blue-sky/home/winding-down';

  const mainImgAlt = isJapaneseRegionalSite
    ? 'woman_sleeping_image_alt'
    : 'tab_item_wind_down_img_alt';

  const { props: bedtimeApproachMaskImageProps } = getImageProps({
    alt: '',
    height: 375,
    src: `blue-sky/home/winding-down-mask-2.jpg`,
    width: 384,
  });

  const { props: mobileMaskImageProps } = getImageProps({
    alt: '',
    height: 277,
    src: `blue-sky/home/wind-down-mask-mobile.jpg`,
    width: 300,
  });

  return (
    <MobileFeature
      title="features_tab_4_label"
      image={{
        alt: mainImgAlt,
        src: mainImage,
      }}
    >
      <div className="relative hidden lg:block" data-cy="wind-down">
        <Image
          alt={t(mainImgAlt)}
          src={mainImage}
          height={547}
          width={400}
          className="rounded-lg object-cover"
          data-cy="wind-down-image"
        />
      </div>
      <div
        className={cx(
          'absolute bottom-16 left-1/2 w-[80vw] max-w-[300px] -translate-x-1/2 flex-col gap-y-6',
          'lg:relative lg:bottom-auto lg:left-0 lg:mt-14 lg:flex lg:w-auto lg:max-w-none lg:translate-x-0',
        )}
      >
        <div className="hidden grid-cols-1 grid-rows-1 lg:grid">
          <Image
            alt=""
            className="col-start-1 col-end-2 row-start-1 row-end-2 ml-[15%] rounded-lg"
            src="blue-sky/home/winding-down-mask-1"
            width={328}
            height={81}
          />
          <Image
            localizedAlt="bedtime_approach_notification_image_alt"
            className="col-start-1 col-end-2 row-start-1 row-end-2 ml-[15%] rounded-lg"
            localizedSrc="bedtime_approach_notification"
            format="png"
            width={328}
            height={81}
          />
        </div>
        <div className="block gap-x-6 lg:flex">
          <div>
            <div
              className="hidden grid-cols-1 grid-rows-1 overflow-hidden rounded-lg bg-cover lg:grid"
              style={{
                backgroundImage: `url(${bedtimeApproachMaskImageProps.src})`,
              }}
            >
              <Image
                localizedAlt="bedtime_approach_app_image_alt"
                className="col-start-1 col-end-2 row-start-1 row-end-2 w-auto self-center py-9"
                localizedSrc="home_scenarios_wind_down_ui"
                format="png"
                width={384}
                height={346}
                data-cy="bedtime-approach-app-ui-image"
              />
            </div>
            <Motion
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1, type: 'tween', delay: 0.5 }}
              className="grid grid-cols-1 grid-rows-1 overflow-hidden rounded-lg bg-cover lg:hidden"
              style={{ backgroundImage: `url(${mobileMaskImageProps.src})` }}
            >
              <Image
                localizedAlt="bedtime_approach_app_image_alt"
                className="col-start-1 col-end-2 row-start-1 row-end-2 w-auto self-center py-4"
                localizedSrc="home_scenarios_wind_down_ui"
                format="png"
                width={300}
                height={270}
                data-cy="bedtime-approach-app-ui-image"
              />
            </Motion>
          </div>
          <div className="hidden rounded-lg border border-gray-300 px-10 py-12 lg:block">
            <QuoteIcon className="text-olive-700" />
            <Typography
              className="mt-4 text-3xl"
              font="serif"
              weight="thin"
              variant="unset"
            >
              <RichText messageKey="features_tab_4_quote" />
            </Typography>
            <Typography
              className="mt-4 text-olive-700"
              variant="body-small"
              color="unset"
            >
              Rhonda C., {t('oura_member')}
            </Typography>
          </div>
        </div>
      </div>
    </MobileFeature>
  );
};

export default WindDown;
